import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fetch as fetchAssets } from "services/assets.service";
import DialogShell from "./DialogShell";
import OptionPicker, { PickerOption } from "./OptionPicker";

// The server caps a page at 100 assets.
const PAGE_SIZE = 100;

/** Every asset that is in stock, page by page. */
async function fetchAssetsInStock(companyId: string): Promise<any[]> {
  const assets: any[] = [];
  for (let page = 1; ; page += 1) {
    const data = await fetchAssets(companyId, { stock: "in stock" }, { page, pageSize: PAGE_SIZE });
    assets.push(...data.assetsCollection);
    if (!data.pagination?.hasNextPage) return assets;
  }
}

type Props = {
  companyId: string;
  employeeName: string;
  onPicked: (assetId: string) => void;
  onClose: () => void;
};

/** «Išduoti turtą», step 1: pick an asset that is in stock. Step 2 is the existing assign form. */
export default function PickAssetDialog({ companyId, employeeName, onPicked, onClose }: Props) {
  const { t } = useTranslation();
  const [assetId, setAssetId] = useState("");
  const [assets, setAssets] = useState<PickerOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchAssetsInStock(companyId)
      .then((inStock) => {
        if (cancelled) return;
        setAssets(
          inStock.map((asset) => ({
            id: asset.id,
            label: asset.name || asset.id,
            detail: [asset.type?.title, asset.brand, asset.model, asset.serial].filter(Boolean).join(" · "),
          }))
        );
      })
      .catch(() => {
        if (!cancelled) toast.error(t("asset_requests_error_generic"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, t]);

  return (
    <DialogShell
      title={t("asset_requests_pick_asset_title")}
      submitLabel={t("asset_requests_pick_asset_next")}
      submitDisabled={!assetId}
      onSubmit={() => onPicked(assetId)}
      onClose={onClose}
    >
      <p className="text-sm text-gray-600">
        {t("asset_requests_for_employee", { name: employeeName })}. {t("asset_requests_pick_asset_hint")}
      </p>
      <OptionPicker
        options={assets}
        selectedId={assetId}
        onSelect={setAssetId}
        searchLabel={t("asset_requests_pick_asset_search")}
        emptyLabel={t("asset_requests_pick_asset_empty")}
        loading={loading}
      />
    </DialogShell>
  );
}
